import cn from 'classnames';
import { type FC, useMemo } from 'react';

import { GroupChipList, type GroupChipListItem } from '@/components/UI/GroupChip/GroupChip';
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import type { GroupApiId } from '@/model/TreatmentGroup.model';
import { useModalAction } from '@/utils/modal';

import { useOverviewAccordion } from '../OverviewAccordionContext';
import styles from './OverviewGroupChips.module.scss';

interface OverviewGroupDetails {
  title: string;
  studyId?: string;
}

interface OverviewGroupChipsProps {
  groupApiIds: Array<GroupApiId> | undefined;
  /** Opens the overview group details modal, including the `+N` chip. */
  groupDetails?: OverviewGroupDetails;
}

/**
 * Resolves group api_ids against the Groups section and renders them as group chips.
 * Ids the Groups section did not return are skipped. With `groupDetails`, the chips
 * and the overflow `+N` chip open the overview group details modal.
 */
const OverviewGroupChips: FC<OverviewGroupChipsProps> = ({ groupApiIds, groupDetails }) => {
  const { groupsByApiId } = useOverviewAccordion();
  const { openModal } = useModalAction();
  const groups = useMemo(() => Array.from(groupsByApiId.values()), [groupsByApiId]);
  const chips = useMemo(
    () =>
      (groupApiIds ?? []).reduce<Array<GroupChipListItem>>((acc, apiId) => {
        const group = groupsByApiId.get(apiId);
        if (_notNil(group)) {
          acc.push({ key: apiId, no: group.no, color: group.color, testId: `overview-group-chip-${apiId}` });
        }
        return acc;
      }, []),
    [groupApiIds, groupsByApiId]
  );

  if (_isEmpty(chips)) {
    return <>-</>;
  }

  if (_isNil(groupDetails)) {
    return <GroupChipList chips={chips} />;
  }

  return (
    <button
      type="button"
      className={cn('link-button', styles.fill)}
      aria-label={`Show groups for ${groupDetails.title}`}
      data-test-component="OverviewGroupChips"
      data-test-element="group-details"
      onClick={() => {
        const visibleGroupIds = (groupApiIds ?? []).filter((apiId) => groupsByApiId.has(apiId));
        const groupCountLabel = visibleGroupIds.length === 1 ? '1 group' : `${visibleGroupIds.length} groups`;
        openModal('OVERVIEW_GROUP_DETAILS', {
          target: { type: 'group', groups: visibleGroupIds },
          groups,
          studyId: groupDetails.studyId ?? null,
          taskTitle: groupDetails.title,
          subText: `Applies to ${groupCountLabel}`,
          emptyMessage: 'No groups found',
        });
      }}
    >
      <GroupChipList chips={chips} />
    </button>
  );
};

export default OverviewGroupChips;
